import type { AttendeesResponse, CalendarRow, CalEvent, NlEventProposal, RsvpStatus } from "@/types";
import { Endpoints, http } from "./http";

export const CalendarApi = {
  listCalendars(): Promise<CalendarRow[]> { return http(Endpoints.calendars); },
  listEvents(range: { start_ts: number; end_ts: number }, calendar_ids?: string[]): Promise<CalEvent[]> {
    const u = new URL(Endpoints.cal_events, window.location.origin);
    u.searchParams.set("start", String(range.start_ts));
    u.searchParams.set("end", String(range.end_ts));
    if (calendar_ids?.length) u.searchParams.set("calendars", calendar_ids.join(","));
    return http(u.pathname + u.search);
  },
  create(ev: Omit<CalEvent, "id" | "created_at" | "updated_at" | "created_by">): Promise<CalEvent> {
    return http(Endpoints.cal_events, { method: "POST", body: JSON.stringify(ev) });
  },
  update(id: string, patch: Partial<CalEvent>): Promise<CalEvent> {
    return http(Endpoints.cal_event_one(id), { method: "PATCH", body: JSON.stringify(patch) });
  },
  delete(id: string): Promise<void> {
    return http(Endpoints.cal_event_one(id), { method: "DELETE" });
  },
  rsvp(event_id: string, status: RsvpStatus): Promise<void> {
    return http(Endpoints.cal_rsvp(event_id), { method: "POST", body: JSON.stringify({ status }) });
  },
  listAttendees(event_id: string): Promise<AttendeesResponse> {
    // 404 here means the caller is neither owner nor attendee (can't see the
    // list); callers should treat that as "render nothing", not a hard error.
    return http(Endpoints.cal_attendees(event_id));
  },
  parseNl(text: string): Promise<{ proposal: NlEventProposal | null; error?: string }> {
    // Pass the browser's IANA timezone so "3pm" is interpreted relative to
    // where the user actually is, not the worker's default (America/Los_
    // Angeles). Without this, an Eastern-tz user typing "3pm" gets back
    // 3pm PT = 18:00 EDT — the "in what world is this 6pm" bug.
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return http(Endpoints.cal_nl_parse, { method: "POST", body: JSON.stringify({ text, timezone }) });
  },
  conflicts(start_ts: number, end_ts: number, attendees: string[]): Promise<CalEvent[]> {
    return http(Endpoints.cal_conflict_check, { method: "POST", body: JSON.stringify({ start_ts, end_ts, attendees }) });
  },
};
